import {defineStore} from "pinia";
import {NotificationEdge, UserSettings} from "@/settings/interfaces";
import {DetectionPatient} from "@/notification/interfaces";
import {consumerCacheApi, storageService} from "@/container";
import dateHelper from "@/core/helpers/dateHelper";
import {
    expandedBounds as computeExpandedBounds,
    NOTIFICATION_WINDOW_WIDTH,
    resolvePlacement,
    WindowRectangle,
} from "@/notification/services/notificationWindowPlacement";
import {supportsClickThrough, supportsWindowPlacement} from "@/core/services/shellCapabilities";
import {SmokingStatus} from "@/customer/interfaces/DeclaredInformation";

export const DETECTION_HEADER_HEIGHT = 80;

/** Collapsed window height: the strip grown to fit the Vitale card button. */
export const DETECTION_COLLAPSED_HEIGHT = DETECTION_HEADER_HEIGHT + 56;

export const useNotificationStore = defineStore('notification', {
    state: () => {
        return {
            isOpen: true,
            shouldShowPanel: true,
            toggleTimeout: null as null | NodeJS.Timeout,
            closeOperationTimeout: null as null | NodeJS.Timeout,
            remainingTimeoutTime: 0,
            startTime: 0,
            headerHeight: DETECTION_HEADER_HEIGHT,
            /** The screen edge the window is pinned to. */
            edge: 'right' as NotificationEdge,
            /**
             * Whether drag & drop repositioning is active. Transient pre-hydration
             * value; applyPlacementSettings sets the real default (on when the shell
             * supports placement, unless the pharmacist opted out).
             */
            dragEnabled: false,
            /** Whether the window is being dragged: the auto-close countdown is held. */
            draggingWindow: false,
            /** The expanded window bounds, memorized to restore them on reopen. */
            expandedBounds: null as WindowRectangle | null,
            settings: null as UserSettings | null,
            modalOpen: false,
            hiddenPolls: {} as Record<number, boolean>,
            /**
             * Smoking status declared by answering a poll, keyed by consumer key.
             * The detection payload carries no declared information, so the answer
             * is kept here to acknowledge it without waiting for a refetch.
             */
            declaredSmoking: {} as Record<string, SmokingStatus>,
            cachedPatients: [] as DetectionPatient[]
        };
    },
    actions: {
        /**
         * Forget the poll answers of the previous detection: the answered flags
         * and the declared smoking status. Called for each new patient
         * detection, whose payload becomes the source of truth. Mutates the
         * records instead of replacing them, components hold a destructured
         * reference to them.
         */
        resetPollState() {
            Object.keys(this.hiddenPolls).forEach((pollId) => {
                delete this.hiddenPolls[Number(pollId)];
            });

            Object.keys(this.declaredSmoking).forEach((consumerKey) => {
                delete this.declaredSmoking[consumerKey];
            });
        },
        /**
         * Record the smoking status just declared for a consumer.
         */
        setDeclaredSmoking(consumerKey: string, status: SmokingStatus) {
            this.declaredSmoking[consumerKey] = status;
        },
        /**
         * Update the pinned screen edge and the drag toggle from the user settings.
         */
        applyPlacementSettings(settings: UserSettings | null | undefined) {
            // resolvePlacement also guards against unsupported stored edges
            // (e.g. 'top' written by an older experimental build).
            this.edge = resolvePlacement(settings).edge;
            // On by default wherever the shell exposes the placement primitives,
            // unless the pharmacist opted out. Older shells without the capability
            // resolve to off, so nothing changes there.
            this.dragEnabled = supportsWindowPlacement() && !settings?.notification?.dragDisabled;
        },
        /**
         * Initialize the toggle timeout with the user settings preferences.
         */
        initToggleTimeout(config: UserSettings) {
            this.settings = config;
            this.applyPlacementSettings(config);

            if (config?.notification.time) {
                this.toggleTimeout = setTimeout(this.closeDrawer, config.notification.time);
                this.startTime = Date.now();
                this.remainingTimeoutTime = config.notification.time;

                // A detection can land mid-drag: hold the countdown it just
                // armed, the end of the drag resumes it.
                if (this.draggingWindow) {
                    this.pauseNotificationTimeout();
                }
            }
        },
        /**
         * Hold the auto-close countdown while the window is dragged, resume it
         * on release. A hard latch (unlike a plain pause): hover changes or
         * fresh detections cannot rearm the countdown mid-drag.
         */
        setWindowDragging(dragging: boolean) {
            this.draggingWindow = dragging;

            if (dragging) {
                this.pauseNotificationTimeout();
            } else {
                this.resumeNotificationTimeout();
            }
        },
        /**
         * Send IPC event to change window hovered state.
         * @param hovered Whether the window is hovered or not.
         */
        windowHoverChange(hovered: boolean) {
            if (hovered) {
                this.pauseNotificationTimeout();
            } else {
                this.resumeNotificationTimeout();
            }

            window.ipcRenderer.windowHoveredChange("notification", hovered);
        },
        /**
         * Pause the notification timeout.
         */
        pauseNotificationTimeout() {
            if (this.toggleTimeout) {
                clearTimeout(this.toggleTimeout);
                this.remainingTimeoutTime = this.calculateRemainingTime();
                // Repeated pauses (hover, then drag) must not subtract the
                // elapsed time twice: restart the elapsed measure.
                this.startTime = Date.now();
            }
        },
        /**
         * Resume the notification timeout.
         */
        resumeNotificationTimeout() {
            if (this.modalOpen || this.draggingWindow) {
                return;
            }

            if (this.remainingTimeoutTime > 0 && this.toggleTimeout) {
                this.toggleTimeout = setTimeout(this.closeDrawer, this.remainingTimeoutTime);
                this.startTime = Date.now();
            }
        },
        /**
         * The time left on the countdown when it gets paused.
         */
        calculateRemainingTime() {
            const elapsedTime = Date.now() - this.startTime;

            return this.remainingTimeoutTime - elapsedTime;
        },
        /**
         * Clear the notification and open the drawer.
         */
        async openDrawer() {
            if (this.toggleTimeout) {
                clearTimeout(this.toggleTimeout);
                this.toggleTimeout = null;
            }

            // A reopen within the close animation window must cancel the pending
            // collapse, which would otherwise fold the panel while it is open.
            if (this.closeOperationTimeout) {
                clearTimeout(this.closeOperationTimeout);
                this.closeOperationTimeout = null;
            }

            if (supportsClickThrough()) {
                // Fixed-bounds mode: the window permanently keeps its expanded
                // bounds — click-through neutralizes the transparent area — so
                // opening is the pure CSS slide, nothing to resize. Resizing a
                // visible transparent window recomposites its previous frame
                // at the new origin (the handle seemed to fly across).
            } else if (supportsWindowPlacement()) {
                if (this.expandedBounds) {
                    // Spread: a reactive proxy cannot cross the context bridge.
                    window.ipcRenderer.setNotificationWindowBounds!({...this.expandedBounds});
                } else {
                    await this.repositionExpanded();
                }
            } else {
                window.ipcRenderer.notificationToggle(true);
            }

            this.isOpen = true;
            this.shouldShowPanel = true;
        },
        /**
         * Reposition the window at the expanded width for the saved placement,
         * used when no expanded bounds were memorized.
         */
        async repositionExpanded() {
            const settings = await storageService().read('userSettings') as UserSettings | null;
            const context = await window.ipcRenderer.getScreenContext!();
            const bounds = computeExpandedBounds(context.display, resolvePlacement(settings), {
                width: NOTIFICATION_WINDOW_WIDTH,
                height: this.headerHeight,
            });

            // show-notification, not a bare set-bounds: the window may have
            // been hidden by the opening sequence and must show back.
            window.ipcRenderer.send('show-notification', {bounds});
        },

        /**
         * Clear the toggle timeout, close the drawer and ask main process to resize the window once animation is done.
         */
        closeDrawer() {
            if (this.toggleTimeout) {
                clearTimeout(this.toggleTimeout);
                this.toggleTimeout = null;
            }

            // Already closed or closing: a second close would make the collapse
            // memorize the retracted tab bounds as the bounds to restore on open.
            if (!this.isOpen) {
                return;
            }

            this.isOpen = false;

            if (this.closeOperationTimeout) {
                clearTimeout(this.closeOperationTimeout);
            }

            this.closeOperationTimeout = setTimeout(() => {
                // The window is resized by NotificationDetection, which measures
                // the rendered strip (its content varies: badge, card button).
                this.shouldShowPanel = false;
            }, 300); // timeout time must match the animation duration
        },
        /**
         * Toggle the notification drawer.
         */
        toggleNotification() {
            if (this.isOpen) {
                this.closeDrawer();
            } else {
                this.openDrawer();
            }
        },
        async loadPatientCache(): Promise<void> {
            const patients = await consumerCacheApi().index();

            this.cachedPatients = patients.map((patient) => {
                return {
                    key: patient.key,
                    firstName: patient.first_name,
                    lastName: patient.last_name,
                    birthDate: patient.birth_date ? dateHelper.detectionFormat(patient.birth_date) : null
                };
            });
        },
    }
});
